import * as React from "react";
import {
  Button,
  Combobox,
  makeStyles,
  Option,
  shorthands,
  tokens,
  useId,
  useComboboxFilter,
  Tooltip,
  Toaster,
  MenuTrigger,
  Menu,
  MenuButton,
  MenuPopover,
  MenuItem,
  MenuList,
  useToastController,
} from "@fluentui/react-components";
import type { ComboboxProps, ToastPosition } from "@fluentui/react-components";
import { Close12Icon } from "..";
import { inject, observer } from "mobx-react";
import Store from "./store";
import { DocumentsFilters } from "./types";
import { InfoIcon } from "..";
import FilterInfo from "./FilterInfo";
import { informUser } from "./utilities";

const useStyles = makeStyles({
  root: {
    // Stack the label above the field with a gap
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    ...shorthands.gap("1em"),
  },
  tagsList: {
    listStyleType: "none",
    marginBottom: tokens.spacingVerticalXXS,
    marginTop: "0.5em",
    paddingLeft: 0,
    display: "flex",
    flexWrap: "wrap",
    gridGap: tokens.spacingHorizontalXXS,
  },
  searchIcon: {
    position: "absolute",
    left: "50px",
    top: "50%",
    transform: "translateY(-50%)", // Center the icon vertically
    zIndex: 1,
  },
  taxGuidanceCombobox: {
    width: "100%",
    "& .fui-Combobox-input": {
      paddingLeft: "2.5em", // Add padding for the icon
    },
    "& .fui-Combobox__input": {
      paddingLeft: "2.5em", // Add padding for the icon
    },
    "& .fui-Combobox__button": {
      display: "none", // Hide the dropdown chevron
    },
    "& .fui-Combobox__expandIcon": {
      display: "none", // Hide the expand icon
    },
    "& svg": {
      display: "none!important", // Hide the default chevron icon
    },
    "& .fui-Combobox-trigger": {
      display: "none!important", // Hide the dropdown chevron
      "& svg": {
        display: "none!important", // Hide the default chevron icon
      },
    },
    "& .fui-Combobox-inputWrapper": {
      position: "relative", // Needed for absolute positioning of the icon
    },
  },
  filterSection: {},
  tabList: {
    marginBottom: "1rem",
  },
  combobox: {
    width: "100%",
  },
  searchBar: {
    width: "100%",
    input: { paddingLeft: "5px" },
    callout: { display: "none" },
  },
  listbox: { maxHeight: "200px" },
  buttonArea: {
    display: "flex",
    flexDirection: "row",
    width: "95%",
    ...shorthands.gap(tokens.spacingHorizontalL),
  },
  label: {
    fontSize: tokens.fontSizeBase200,
  },
});

export const Filters = inject("store")(
  observer(({ store, handleOpen, props }: { store: Store; handleOpen: () => void; props?: Partial<ComboboxProps> }) => {
    const classes = useStyles();
    const toasterId = useId("filter_toaster");
    const { dispatchToast } = useToastController(toasterId);
    const caseLawComboId = useId("law-combo-multi");
    const clientWorkRelatedLawComboId = useId("clientWorkRelatedLaw-combo-multi");
    const caseLawSelectedListId = `${caseLawComboId}-selection`;
    const sectionComboId = useId("section-combo-multi");
    const sectionSelectedListId = `${sectionComboId}-selection`;
    const clientWorkRelatedLawSelectedListId = `${clientWorkRelatedLawComboId}-selection`;
    const caseLawKeywordComboId = useId("caseLawKeyword-combo-multi");
    const caseLawKeywordSelectedListId = `${caseLawKeywordComboId}-selection`;
    const clientWorkKeywordComboId = useId("clientWorkKeyword-combo-multi");
    const clientWorkKeywordSelectedListId = `${clientWorkKeywordComboId}-selection`;
    const taxKeywordComboId = useId("taxKeyword-combo-multi");
    const taxKeywordSelectedListId = `${taxKeywordComboId}-selection`;
    const taxComboId = useId("tax-combo-multi");
    const taxGuidanceId = useId("taxGuidance-combo-multi");
    const taxGuidanceComboId = useId("taxGuidance-combo-multi");
    const taxGuidanceSelectedListId = `${taxGuidanceComboId}-selection`;
    const taxSelectedListId = `${taxComboId}-selection`;
    const classComboId = useId("class-combo-multi");
    const classSelectedListId = `${classComboId}-selection`;
    const [selectedValue, setSelectedValue] = React.useState<string>("Case Law Filters");
    const caseLawSelectedListRef = React.useRef<HTMLUListElement>(null);
    const caseLawComboboxInputRef = React.useRef<HTMLInputElement>(null);
    const clientWorkRelatedLawSelectedListRef = React.useRef<HTMLUListElement>(null);
    const clientWorkRelatedLawComboboxInputRef = React.useRef<HTMLInputElement>(null);
    const sectionSelectedListRef = React.useRef<HTMLUListElement>(null);
    const sectionComboboxInputRef = React.useRef<HTMLInputElement>(null);
    const caseLawKeywordSelectedListRef = React.useRef<HTMLUListElement>(null);
    const caseLawKeywordComboboxInputRef = React.useRef<HTMLInputElement>(null);
    const clientWorkKeywordSelectedListRef = React.useRef<HTMLUListElement>(null);
    const clientWorkKeywordComboboxInputRef = React.useRef<HTMLInputElement>(null);
    const taxKeywordSelectedListRef = React.useRef<HTMLUListElement>(null);
    const taxKeywordComboboxInputRef = React.useRef<HTMLInputElement>(null);
    const taxSelectedListRef = React.useRef<HTMLUListElement>(null);
    const taxGuidanceInputRef = React.useRef<HTMLInputElement>(null);
    const taxGuidanceSelectedListRef = React.useRef<HTMLUListElement>(null);
    const taxComboboxInputRef = React.useRef<HTMLInputElement>(null);
    const classComboboxInputRef = React.useRef<HTMLInputElement>(null);
    const classSelectedListRef = React.useRef<HTMLUListElement>(null);
    const [position] = React.useState<ToastPosition>("top");
    const properties = store.properties;
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    //  const documentOptions = properties?.document_types ? properties.document_types : [];
    const caseLawOptions = properties?.case_laws?.laws ?? [];
    const clientWorkRelatedLawOptions = properties?.client_work?.related_laws ?? [];
    const caseLawKeywordOptions = properties?.case_laws.keywords ?? [];
    const clientWorkKeywordOptions = properties?.client_work.keywords ?? [];
    const taxKeywordOptions = properties?.tax_guidances.keywords ?? [];

    const taxCategoryOptions = properties?.tax_guidances.tax_categories ?? [];
    const taxGuidanceOptions = properties?.tax_guidances.tax_guidances ?? [];
    const documentClassOptions = properties?.client_work.document_classes ?? [];
    //    const scopeOptions = properties?.tax ? properties.tax : [];

    // Handle selectedOptions both when an option is selected or deselected in the Combobox,
    // and when an option is removed by clicking on a tag
    const [selectedOptions, setSelectedOptions] = React.useState<DocumentsFilters>(store.getFilters());

    React.useEffect(() => {
      setSelectedOptions(store.getFilters());
    }, [store.filters]);
    React.useEffect(() => {
      if (properties) {
        setIsLoading(false);
      } else {
        setIsLoading(true);
      }
    }, [properties]);

    const [taxLawMapping, setTaxLawMapping] = React.useState<string[]>([
      selectedOptions.case_laws.laws.length > 0
        ? properties?.law_section_mapping[selectedOptions.case_laws.laws[0]]
        : [],
    ]);

    const [caseLawQuery, setCaseLawQuery] = React.useState<string>("");
    const [sectionQuery, setSectionQuery] = React.useState<string>("");
    const [clientWorkRelatedLawQuery, setClientWorkRelatedLawQuery] = React.useState<string>("");
    const caseLawChildren = useComboboxFilter(caseLawQuery, caseLawOptions, {
      noOptionsMessage: isLoading ? "Loading" : "No laws match your search.",
      renderOption: (option) => (
        <Option key={option} text="case_laws.laws" value={option}>
          {typeof option === "string" ? option : ""}
        </Option>
      ),
    });
    const sectionChildren = useComboboxFilter(sectionQuery, taxLawMapping, {
      noOptionsMessage: "No sections match your search.",
      renderOption: (option) => (
        <Option key={option} text="case_laws.sections" value={option}>
          {option}
        </Option>
      ),
    });
    const clientWorkRelatedLawChildren = useComboboxFilter(clientWorkRelatedLawQuery, clientWorkRelatedLawOptions, {
      noOptionsMessage: isLoading ? "Loading" : "No laws match your search.",
      renderOption: (option) => (
        <Option key={option} text="client_work.related_laws" value={option}>
          {option}
        </Option>
      ),
    });

    const [taxKeywordQuery, setTaxKeywordQuery] = React.useState<string>("");
    const [clientWorkKeywordQuery, setClientWorkKeywordQuery] = React.useState<string>("");
    const [caseLawKeywordQuery, setCaseLawKeywordQuery] = React.useState<string>("");
    const sectionLabelledBy =
      selectedOptions.case_laws.sections.length > 0 ? `${sectionComboId} ${sectionSelectedListId}` : sectionComboId;
    const caseLawKeywordLabelledBy =
      selectedOptions.case_laws.keywords.length > 0
        ? `${caseLawKeywordComboId} ${caseLawKeywordSelectedListId}`
        : caseLawKeywordComboId;

    const taxCategoryLabelledBy =
      selectedOptions.tax_guidances.tax_categories.length > 0 ? `${taxComboId} ${taxSelectedListId}` : taxComboId;
    const [classQuery, setClassQuery] = React.useState<string>("");
    const taxKeywordChildren = useComboboxFilter(
      taxKeywordQuery,
      taxKeywordOptions,

      {
        noOptionsMessage: isLoading ? "Loading..." : "No keywords match your search.",
        renderOption: (option) => (
          <Option key={option} text="tax_guidances.keywords" value={option}>
            {option}
          </Option>
        ),
      }
    );
    const [taxCategoryQuery, setTaxCategoryQuery] = React.useState<string>("");
    const [taxGuidanceQuery, setTaxGuidanceQuery] = React.useState<string>("");
    const taxChildren = useComboboxFilter(taxCategoryQuery, taxCategoryOptions, {
      noOptionsMessage: isLoading ? "Loading..." : "No taxes match your search.",
      renderOption: (option) => (
        <Option key={option} text="tax_guidances.tax_categories" value={option}>
          {option}
        </Option>
      ),
    });

    const caseLawKeywordChildren = useComboboxFilter(
      caseLawKeywordQuery,
      caseLawKeywordOptions,

      {
        noOptionsMessage: isLoading ? "Loading..." : "No keywords match your search.",
        renderOption: (option) => (
          <Option key={option} text="case_laws.keywords" value={option}>
            {option}
          </Option>
        ),
      }
    );
    const clientWorkKeywordChildren = useComboboxFilter(
      clientWorkKeywordQuery,
      clientWorkKeywordOptions,

      {
        noOptionsMessage: isLoading ? "Loading..." : "No keywords match your search.",
        renderOption: (option) => (
          <Option key={option} text="client_work.keywords" value={option}>
            {option}
          </Option>
        ),
      }
    );

    const documentClassChildren = useComboboxFilter(classQuery, documentClassOptions, {
      noOptionsMessage: isLoading ? "Loading..." : "No classes match your search.",
      renderOption: (option) => (
        <Option key={option} text="client_work.document_classes" value={option}>
          {option}
        </Option>
      ),
    });
    const taxGuidanceChildren = useComboboxFilter(taxGuidanceQuery, taxGuidanceOptions, {
      noOptionsMessage: isLoading ? "Loading..." : "No tax guidances match your search.",
      renderOption: (option) => (
        <Option key={option} text="tax_guidances.tax_guidances" value={option}>
          {option}
        </Option>
      ),
    });

    const onSelect: ComboboxProps["onOptionSelect"] = (_event, data) => {
      const [parent, child] = data.optionText.split(".");
      if (child) {
        setSelectedOptions({
          ...selectedOptions,
          [parent]: {
            ...selectedOptions[parent],
            [child]: data.selectedOptions,
          },
        });
      } else {
        setSelectedOptions({
          ...selectedOptions,
          [data.optionText]: data.selectedOptions,
        });
      }

      if (data.optionText === "case_laws.laws") {
        setTaxLawMapping(properties?.law_section_mapping[data.selectedOptions[0]]);
      }
      setCaseLawQuery("");
      setClientWorkRelatedLawQuery("");
      setCaseLawKeywordQuery("");
      setClientWorkKeywordQuery("");
      setTaxKeywordQuery("");
      setTaxCategoryQuery("");
      setClassQuery("");
    };

    function handleApply() {
      const hasTaxhallinonOhjeet = selectedValue === "Tax Guidance Filters";
      const hasOikeustapaukset = selectedValue === "Case Law Filters";
      const hasTaxOrKeywords =
        selectedOptions.tax_guidances.tax_categories.length > 0 ||
        selectedOptions.tax_guidances.keywords.length > 0 ||
        selectedOptions.tax_guidances.tax_guidances.length > 0;
      const hasLawOrKeywords =
        selectedOptions.case_laws.laws.length > 0 || selectedOptions.case_laws.keywords.length > 0;
      const hasClientWork = selectedValue === "Client Work Filters";
      const hasClassAndLawOrKeyword =
        selectedOptions.client_work.document_classes.length > 0 &&
        (selectedOptions.case_laws.keywords.length > 0 ||
          selectedOptions.client_work.keywords.length > 0 ||
          selectedOptions.client_work.related_laws.length > 0 ||
          selectedOptions.case_laws.laws.length > 0);

      if (!selectedOptions) {
        informUser("Please select at least one filter.", dispatchToast, position);
        return;
      }
      if (hasTaxhallinonOhjeet && !hasTaxOrKeywords) {
        informUser(
          "Please select at least one tax or keyword filter when selecting Taxhallinnon ohjeet.",
          dispatchToast,
          position
        );
        return;
      }

      if (hasOikeustapaukset && !hasLawOrKeywords) {
        informUser(
          "Please select at least one law or keyword filter when selecting Oikeustapaukset.",
          dispatchToast,
          position
        );
        return;
      }

      if (hasClientWork && !hasClassAndLawOrKeyword) {
        informUser(
          "Please select at least one class filter and either at least one keyword or law filter when selecting Asiakastyö.",
          dispatchToast,
          position
        );
        return;
      }
      try {
        store.setFilters(selectedOptions);

        store.fetchFilteredDocuments();

        store.setFiltersApplied(true);
      } catch (error) {
        console.error("Error setting filters:", error);
      }

      handleOpen();
    }
    const onTagClick = (property: string, option: string, index: number) => {
      // remove selected option

      const [parent, child] = property.split(".");

      if (child) {
        setSelectedOptions({
          ...selectedOptions,
          [parent]: {
            ...selectedOptions[parent],
            [child]: selectedOptions[parent][child].filter((item: string) => item !== option),
          },
        });
      }
      // focus previous or next option, defaulting to focusing back to the combo input
      const indexToFocus = index === 0 ? 1 : index - 1;
      const optionToFocus = {
        case_laws: caseLawSelectedListRef.current?.querySelector(`#${caseLawComboId}-remove-${indexToFocus}`),
        client_work_related_laws: clientWorkRelatedLawSelectedListRef.current?.querySelector(
          `#${clientWorkRelatedLawComboId}-remove-${indexToFocus}`
        ),
        case_laws_keywords: caseLawKeywordSelectedListRef.current?.querySelector(
          `#${caseLawKeywordComboId}-remove-${indexToFocus}`
        ),
        client_work_keywords: clientWorkKeywordSelectedListRef.current?.querySelector(
          `#${clientWorkKeywordComboId}-remove-${indexToFocus}`
        ),
        tax_keywords: taxKeywordSelectedListRef.current?.querySelector(`#${taxKeywordComboId}-remove-${indexToFocus}`),
        tax: taxSelectedListRef.current?.querySelector(`#${taxComboId}-remove-${indexToFocus}`),
        sections: sectionSelectedListRef.current?.querySelector(`#${sectionComboId}-remove-${indexToFocus}`),
        tax_guidances: taxGuidanceSelectedListRef.current?.querySelector(
          `#${taxGuidanceComboId}-remove-${indexToFocus}`
        ),

        classes: classSelectedListRef.current?.querySelector(`#${classComboId}-remove-${indexToFocus}`),
      }[property];

      if (optionToFocus) {
        (optionToFocus as HTMLButtonElement).focus();
      } else {
        ({
          case_laws: caseLawComboboxInputRef,
          sections: sectionComboboxInputRef,
          client_work_related_laws: clientWorkRelatedLawComboboxInputRef,
          case_laws_keywords: caseLawKeywordComboboxInputRef,
          client_work_keywords: clientWorkKeywordComboboxInputRef,
          tax_keywords: taxKeywordComboboxInputRef,
          tax: taxComboboxInputRef,
          tax_guidances: taxGuidanceSelectedListRef,
          classes: classComboboxInputRef,
        })[property].current?.focus();
      }
    };

    const onReset = () => {
      store.resetFilters();
      setSelectedOptions(store.getFilters());
    };

    const caseLawLabelledBy =
      selectedOptions.case_laws.laws.length > 0 ? `${caseLawComboId} ${caseLawSelectedListId}` : caseLawComboId;
    const clientWorkRelatedLawLabelledBy =
      selectedOptions.client_work.related_laws.length > 0
        ? `${clientWorkRelatedLawComboId} ${clientWorkRelatedLawSelectedListId}`
        : clientWorkRelatedLawComboId;
    const clientWorkKeywordLabelledBy =
      selectedOptions.client_work.keywords.length > 0
        ? `${clientWorkKeywordComboId} ${clientWorkKeywordSelectedListId}`
        : clientWorkKeywordComboId;
    const taxKeywordLabelledBy =
      selectedOptions.tax_guidances.keywords.length > 0
        ? `${taxKeywordComboId} ${taxKeywordSelectedListId}`
        : taxKeywordComboId;
    const taxGuidanceLabelledBy =
      selectedOptions.tax_guidances.tax_categories.length > 0 ? `${taxComboId} ${taxSelectedListId}` : taxComboId;

    const classLabelledBy =
      selectedOptions.client_work.document_classes.length > 0 ? `${classComboId} ${classSelectedListId}` : classComboId;

    const getButtonStyle = () => ({
      width: "20px",
      height: "20px",
      padding: "0",
      border: "none",
      margin: "0 0.5em",
    });

    return (
      <div className={classes.root}>
        <Toaster toasterId={toasterId} />
        <div style={{ display: "flex", marginLeft: "3px", alignItems: "center" }}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Tooltip content="Large with calendar icon only" relationship="label">
                <MenuButton size="medium" appearance="outline">
                  {String(selectedValue)}
                </MenuButton>
              </Tooltip>
            </MenuTrigger>

            <MenuPopover>
              <MenuList style-={{ marginRight: "0.5rem" }}>
                <MenuItem
                  id="FilterCaseLaw"
                  style={{ width: "100%", fontSize: "12pt", font: "Arial", fontWeight: "normal" }}
                  content="Case Law Filters"
                  onClick={() => setSelectedValue("Case Law Filters")}
                >
                  {" "}
                  Case Law Filters
                </MenuItem>
                <MenuItem
                  id="FilterTaxGuidance"
                  content="Tax Guidance Filters"
                  style={{ width: "100%", fontSize: "12pt", font: "Arial", fontWeight: "normal" }}
                  onClick={() => setSelectedValue("Tax Guidance Filters")}
                >
                  Tax Guidance Filters
                </MenuItem>
                <MenuItem
                  id="FilterClientWork"
                  content="Client Work Filters"
                  style={{ width: "100%", fontSize: "12pt", font: "Arial", fontWeight: "normal" }}
                  onClick={() => setSelectedValue("Client Work Filters")}
                >
                  Client Work Filters
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          <FilterInfo />
        </div>
        {/* // Law filters */}
        {selectedValue === "Case Law Filters" && (
          <div className={classes.filterSection}>
            <div className={classes.combobox}>
              <label className={classes.label} id={caseLawComboId}>
                Law{" "}
              </label>
              <Tooltip
                content={{
                  children: "Filter court cases by M-Files court case reference.",
                }}
                positioning="above-start"
                withArrow
                relationship="label"
              >
                <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
              </Tooltip>
              {selectedOptions.case_laws.laws.length ? (
                <ul id={caseLawSelectedListId} className={classes.tagsList} ref={caseLawSelectedListRef}>
                  <span id={`${caseLawComboId}-remove`} hidden>
                    Remove
                  </span>
                  {selectedOptions.case_laws.laws.map((option, i) => (
                    <li key={option}>
                      <Button
                        size="small"
                        shape="rounded"
                        appearance="primary"
                        icon={<Close12Icon />}
                        iconPosition="after"
                        onClick={() => onTagClick("case_laws.laws", option, i)}
                        id={`${caseLawComboId}-remove-${i}`}
                        aria-labelledby={`${caseLawComboId}-remove ${caseLawComboId}-remove-${i}`}
                      >
                        {option}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Combobox
                className={classes.combobox}
                aria-labelledby={caseLawLabelledBy}
                listbox={{ className: classes.listbox }}
                positioning={"below"}
                onChange={(ev) => setCaseLawQuery(ev.target.value)}
                multiselect={true}
                placeholder="Select one or more laws"
                selectedOptions={selectedOptions.case_laws.laws}
                onOptionSelect={onSelect}
                ref={caseLawComboboxInputRef}
                {...props}
              >
                {caseLawChildren}
              </Combobox>
            </div>

            {/* // Section filters FIX TO MAP w LAWS*/}

            {selectedOptions.case_laws.laws.length > 0 && (
              <div className={classes.combobox} style={{ padding: "2px" }}>
                <label className={classes.label} id={sectionComboId}>
                  Section
                </label>
                <Tooltip
                  content={{
                    children: "Filter data sources by section.",
                  }}
                  positioning="above-start"
                  withArrow
                  relationship="label"
                >
                  <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
                </Tooltip>
                {selectedOptions.case_laws.sections.length ? (
                  <ul id={sectionSelectedListId} className={classes.tagsList} ref={sectionSelectedListRef}>
                    <span id={`${sectionComboId}-remove`} hidden>
                      Remove
                    </span>
                    {selectedOptions.case_laws.sections.map((option, i) => (
                      <li key={option}>
                        <Button
                          size="small"
                          shape="rounded"
                          appearance="primary"
                          icon={<Close12Icon />}
                          iconPosition="after"
                          onClick={() => onTagClick("case_laws.sections", option, i)}
                          id={`${sectionComboId}-remove-${i}`}
                          aria-labelledby={`${sectionComboId}-remove ${sectionComboId}-remove-${i}`}
                        >
                          {option}
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <Combobox
                  className={classes.combobox}
                  aria-labelledby={sectionLabelledBy}
                  listbox={{ className: classes.listbox }}
                  positioning={"below"}
                  onChange={(ev) => setSectionQuery(ev.target.value)}
                  multiselect={true}
                  placeholder="Select one or more sections"
                  selectedOptions={selectedOptions.case_laws.sections}
                  onOptionSelect={onSelect}
                  ref={sectionComboboxInputRef}
                  {...props}
                >
                  {sectionChildren}
                </Combobox>
              </div>
            )}

            {/*Case Law Keyword filters */}

            <div className={classes.combobox} style={{ padding: "2px" }}>
              <label className={classes.label} id={caseLawKeywordComboId}>
                Keywords
              </label>
              <Tooltip
                content={{
                  children: "Filter Data Sources by Case Law Keywords",
                }}
                positioning="above-start"
                withArrow
                relationship="label"
              >
                <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
              </Tooltip>
              {selectedOptions.case_laws.keywords.length ? (
                <ul id={caseLawKeywordSelectedListId} className={classes.tagsList} ref={caseLawKeywordSelectedListRef}>
                  <span id={`${caseLawKeywordComboId}-remove`} hidden>
                    Remove
                  </span>
                  {selectedOptions.case_laws.keywords.map((option, i) => (
                    <li key={option}>
                      <Button
                        size="small"
                        shape="rounded"
                        appearance="primary"
                        icon={<Close12Icon />}
                        iconPosition="after"
                        onClick={() => onTagClick("case_laws.keywords", option, i)}
                        id={`${caseLawKeywordComboId}-remove-${i}`}
                        aria-labelledby={`${caseLawKeywordComboId}-remove ${caseLawKeywordComboId}-remove-${i}`}
                      >
                        {option}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}

              <Combobox
                className={classes.combobox}
                aria-labelledby={caseLawKeywordLabelledBy}
                listbox={{ className: classes.listbox }}
                onChange={(ev) => setCaseLawKeywordQuery(ev.target.value)}
                positioning={"below"}
                multiselect={true}
                placeholder="Select one or more keywords"
                selectedOptions={selectedOptions.case_laws.keywords}
                onOptionSelect={onSelect}
                ref={caseLawKeywordComboboxInputRef}
                {...props}
              >
                {caseLawKeywordChildren}
              </Combobox>
            </div>
          </div>
        )}

        {/* Class filters */}
        {selectedValue === "Client Work Filters" && (
          <div className={classes.filterSection}>
            <div className={classes.combobox} style={{ padding: "2px" }}>
              <label className={classes.label} id={classComboId}>
                Document Class
              </label>
              <Tooltip
                content={{
                  children: "Filter data sources by class.",
                }}
                positioning="above-start"
                withArrow
                relationship="label"
              >
                <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
              </Tooltip>
              {selectedOptions.client_work.document_classes.length ? (
                <ul id={classSelectedListId} className={classes.tagsList} ref={classSelectedListRef}>
                  <span id={`${classComboId}-remove`} hidden>
                    Remove
                  </span>
                  {selectedOptions.client_work.document_classes.map((option, i) => (
                    <li key={option}>
                      <Button
                        size="small"
                        shape="rounded"
                        appearance="primary"
                        icon={<Close12Icon />}
                        iconPosition="after"
                        onClick={() => onTagClick("client_work.document_classes", option, i)}
                        id={`${classComboId}-remove-${i}`}
                        aria-labelledby={`${classComboId}-remove ${classComboId}-remove-${i}`}
                      >
                        {option}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Combobox
                className={classes.combobox}
                aria-labelledby={classLabelledBy}
                listbox={{ className: classes.listbox }}
                onChange={(ev) => setClassQuery(ev.target.value)}
                positioning={"below"}
                multiselect={true}
                placeholder="Select one or more classes"
                selectedOptions={selectedOptions.client_work.document_classes}
                onOptionSelect={onSelect}
                ref={classComboboxInputRef}
                {...props}
              >
                {documentClassChildren}
              </Combobox>
            </div>
            {/*Client Work Related Law filters */}
            <div className={classes.combobox}>
              <label className={classes.label} id={clientWorkRelatedLawComboId}>
                Client Work Related Laws
              </label>
              <Tooltip
                content={{
                  children: "Filter client work by related law.",
                }}
                positioning="above-start"
                withArrow
                relationship="label"
              >
                <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
              </Tooltip>
              {selectedOptions.client_work.related_laws.length ? (
                <ul
                  id={clientWorkRelatedLawSelectedListId}
                  className={classes.tagsList}
                  ref={clientWorkRelatedLawSelectedListRef}
                >
                  <span id={`${clientWorkRelatedLawComboId}-remove`} hidden>
                    Remove
                  </span>
                  {selectedOptions.client_work.related_laws.map((option, i) => (
                    <li key={option}>
                      <Button
                        size="small"
                        shape="rounded"
                        appearance="primary"
                        icon={<Close12Icon />}
                        iconPosition="after"
                        onClick={() => onTagClick("client_work_related_laws", option, i)}
                        id={`${clientWorkRelatedLawComboId}-remove-${i}`}
                        aria-labelledby={`${clientWorkRelatedLawComboId}-remove ${clientWorkRelatedLawComboId}-remove-${i}`}
                      >
                        {option}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Combobox
                className={classes.combobox}
                aria-labelledby={clientWorkRelatedLawLabelledBy}
                listbox={{ className: classes.listbox }}
                positioning={"below"}
                onChange={(ev) => setClientWorkRelatedLawQuery(ev.target.value)}
                multiselect={true}
                placeholder="Select one or more laws"
                selectedOptions={selectedOptions.client_work.related_laws}
                onOptionSelect={onSelect}
                ref={clientWorkRelatedLawComboboxInputRef}
                {...props}
              >
                {clientWorkRelatedLawChildren}
              </Combobox>
            </div>

            {/*Client Work Keyword filters */}

            <div className={classes.combobox} style={{ padding: "2px" }}>
              <label className={classes.label} id={clientWorkKeywordComboId}>
                Keywords
              </label>
              <Tooltip
                content={{
                  children: "Filter Data Sources by Client Work Keywords",
                }}
                positioning="above-start"
                withArrow
                relationship="label"
              >
                <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
              </Tooltip>
              {selectedOptions.client_work.keywords.length ? (
                <ul
                  id={clientWorkKeywordSelectedListId}
                  className={classes.tagsList}
                  ref={clientWorkKeywordSelectedListRef}
                >
                  <span id={`${clientWorkKeywordComboId}-remove`} hidden>
                    Remove
                  </span>
                  {selectedOptions.client_work.keywords.map((option, i) => (
                    <li key={option}>
                      <Button
                        size="small"
                        shape="rounded"
                        appearance="primary"
                        icon={<Close12Icon />}
                        iconPosition="after"
                        onClick={() => onTagClick("client_work.keywords", option, i)}
                        id={`${clientWorkKeywordComboId}-remove-${i}`}
                        aria-labelledby={`${clientWorkKeywordComboId}-remove ${clientWorkKeywordComboId}-remove-${i}`}
                      >
                        {option}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}

              <Combobox
                className={classes.combobox}
                aria-labelledby={clientWorkKeywordLabelledBy}
                listbox={{ className: classes.listbox }}
                onChange={(ev) => setClientWorkKeywordQuery(ev.target.value)}
                positioning={"below"}
                multiselect={true}
                placeholder="Select one or more keywords"
                selectedOptions={selectedOptions.client_work.keywords}
                onOptionSelect={onSelect}
                ref={clientWorkKeywordComboboxInputRef}
                {...props}
              >
                {clientWorkKeywordChildren}
              </Combobox>
            </div>
          </div>
        )}

        {/*Tax filters*/}
        {selectedValue === "Tax Guidance Filters" && (
          <div className={classes.combobox} style={{ padding: "2px" }}>
            <label className={classes.label} id={taxKeywordComboId}>
              Tax Guidances
            </label>
            <Tooltip
              content={{
                children: "Filter data sources by tax guidances.",
              }}
              positioning="above-start"
              withArrow
              relationship="label"
            >
              <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
            </Tooltip>
            {selectedOptions.tax_guidances.tax_guidances.length ? (
              <ul id={taxGuidanceSelectedListId} className={classes.tagsList} ref={taxGuidanceSelectedListRef}>
                <span id={`${taxGuidanceId}-remove`} hidden>
                  Remove
                </span>
                {selectedOptions.tax_guidances.tax_guidances.map((option, i) => (
                  <li key={option}>
                    <Button
                      size="small"
                      shape="rounded"
                      appearance="primary"
                      icon={<Close12Icon />}
                      iconPosition="after"
                      onClick={() => onTagClick("tax_guidances.tax_guidances", option, i)}
                      id={`${taxGuidanceId}-remove-${i}`}
                      aria-labelledby={`${taxGuidanceId}-remove ${taxGuidanceId}-remove-${i}`}
                    >
                      {option}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}

            <Combobox
              id={"combobox-tax-guidance"}
              aria-labelledby={taxGuidanceLabelledBy}
              positioning={"below"}
              className={classes.combobox}
              listbox={{ className: classes.listbox }}
              freeform
              multiselect={true}
              onChange={(ev) => setTaxGuidanceQuery(ev.target.value)}
              selectedOptions={selectedOptions.tax_guidances.tax_guidances}
              onOptionSelect={onSelect}
              placeholder="Search tax guidances"
              autoComplete="off"
              ref={taxGuidanceInputRef}
              {...props}
            >
              {taxGuidanceChildren}
            </Combobox>

            <div className={classes.combobox} style={{ padding: "2px" }}>
              <label className={classes.label} id={taxComboId}>
                Tax Category
              </label>
              <Tooltip
                content={{
                  children: "Filter data sources by tax category.",
                }}
                positioning="above-start"
                withArrow
                relationship="label"
              >
                <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
              </Tooltip>
              {selectedOptions.tax_guidances.tax_categories.length ? (
                <ul id={taxSelectedListId} className={classes.tagsList} ref={taxSelectedListRef}>
                  <span id={`${taxComboId}-remove`} hidden>
                    Remove
                  </span>
                  {selectedOptions.tax_guidances.tax_categories.map((option, i) => (
                    <li key={option}>
                      <Button
                        size="small"
                        shape="rounded"
                        appearance="primary"
                        icon={<Close12Icon />}
                        iconPosition="after"
                        onClick={() => onTagClick("tax_guidances.tax_categories", option, i)}
                        id={`${taxComboId}-remove-${i}`}
                        aria-labelledby={`${taxComboId}-remove ${taxComboId}-remove-${i}`}
                      >
                        {option}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Combobox
                className={classes.combobox}
                aria-labelledby={taxCategoryLabelledBy}
                listbox={{ className: classes.listbox }}
                onChange={(ev) => setTaxCategoryQuery(ev.target.value)}
                positioning={"below"}
                multiselect={true}
                placeholder="Select one or more tax categories"
                selectedOptions={selectedOptions.tax_guidances.tax_categories}
                onOptionSelect={onSelect}
                ref={taxComboboxInputRef}
                {...props}
              >
                {taxChildren}
              </Combobox>
            </div>
            {/*Tax Administration Keyword filters */}
            <div className={classes.combobox} style={{ padding: "2px" }}>
              <label className={classes.label} id={taxKeywordComboId}>
                Keywords
              </label>
              <Tooltip
                content={{
                  children: "Filter data sources by tax guidance keywords",
                }}
                positioning="above-start"
                withArrow
                relationship="label"
              >
                <Button icon={<InfoIcon />} style={getButtonStyle()}></Button>
              </Tooltip>
              {selectedOptions.tax_guidances.keywords.length ? (
                <ul id={taxKeywordSelectedListId} className={classes.tagsList} ref={taxKeywordSelectedListRef}>
                  <span id={`${taxKeywordComboId}-remove`} hidden>
                    Remove
                  </span>
                  {selectedOptions.tax_guidances.keywords.map((option, i) => (
                    <li key={option}>
                      <Button
                        size="small"
                        shape="rounded"
                        appearance="primary"
                        icon={<Close12Icon />}
                        iconPosition="after"
                        onClick={() => onTagClick("tax_guidances.keywords", option, i)}
                        id={`${taxKeywordComboId}-remove-${i}`}
                        aria-labelledby={`${taxKeywordComboId}-remove ${taxKeywordComboId}-remove-${i}`}
                      >
                        {option}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}

              <Combobox
                className={classes.combobox}
                aria-labelledby={taxKeywordLabelledBy}
                listbox={{ className: classes.listbox }}
                onChange={(ev) => setTaxKeywordQuery(ev.target.value)}
                positioning={"below"}
                multiselect={true}
                placeholder="Select one or more keywords"
                selectedOptions={selectedOptions.tax_guidances.keywords}
                onOptionSelect={onSelect}
                ref={taxKeywordComboboxInputRef}
                {...props}
              >
                {taxKeywordChildren}
              </Combobox>
            </div>
          </div>
        )}

        <div className={classes.buttonArea}>
          <Button appearance="primary" onClick={handleApply}>
            Apply
          </Button>
          <Button appearance="secondary" onClick={onReset}>
            Reset all filters
          </Button>
        </div>
      </div>
    );
  })
);

export default observer(Filters);
